import React from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import DateInput from "../../../../components/form-group/date-input";
import RadioSelect from "../../../../components/radio-select";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { Customer, LeadDemographic } from "../../../../core/api/models";
import "react-widgets/dist/css/react-widgets.css";
import nationalities from "../../../../utils/nationality.json";
import Occupations from "../../../../utils/occupations.json";
import CustomerLanguage from "../../../../components/customer-language";
import CustomerChildren from "../../../../components/customer-children";
import validator from "validator";
import { capitalizeFirstLetter, AgeLimit } from "../../../../core";
import moment from "moment";

const bloodType = [
  { name: "A+ve" },
  { name: "A-ve" },
  { name: "B+ve" },
  { name: "B-ve" },
  { name: "O+ve" },
  { name: "O-ve" },
  { name: "AB+ve" },
  { name: "AB-ve" },
];

const incomeRange = [
  { name: "No Income Category", value: "" },
  { name: "KD 199 and Below", value: "KD 199 and Below" },
  { name: "KD 200 - 299", value: "KD 200 - 299" },
  { name: "KD 300 - 399", value: "KD 300 - 399" },
  { name: "KD 400 - 499", value: "KD 400 - 499" },
  { name: "KD 500 - 599", value: "KD 500 - 599" },
  { name: "KD 600 - 799", value: "KD 600 - 799" },
  { name: "KD 800 - 999", value: "KD 800 - 999" },
  { name: "KD 1000 - 1199", value: "KD 1000 - 1199" },
  { name: "KD 1200 and above", value: "KD 1200 and above" },
];

const DemographicForm: React.FunctionComponent<{
  onSubmit: (data?: any) => void;
  customerDetails?: Customer;
  canUpdateCustomerDemographics?: boolean;
  isSaving?: boolean;
  fields: { displayName: string; value: string; type: string; group: string }[];
}> = (props) => {
  const [isAlghanimEmployee, setAlghanimEmployee] = React.useState<boolean>(
    props.customerDetails?.isAlghanimEmployee || false
  );

  const {
    control,
    handleSubmit,
    errors,
    setValue,
    watch,
  } = useForm<LeadDemographic>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  React.useEffect(() => {
    if (watch("dob")) {
      const year = moment().diff(watch("dob"), "years", true);
      const ageRange = AgeLimit?.find((a) => a.from <= year && a.to >= year);
      setValue("ageRange", ageRange?.name);
    }
  }, [watch("dob")]);

  const tag = props.customerDetails?.tags ? props.customerDetails?.tags[0] : "";

  const onAlghanimEmployeeChange = (value: boolean) => {
    setAlghanimEmployee(value);
  };

  const children = {
    numberOfChildren: props.customerDetails?.numberOfChildren,
    children: props.customerDetails?.children,
  };

  const languagesSpeak = props.customerDetails?.languagesSpeak?.length
    ? props.customerDetails?.languagesSpeak.map((lng: string) => {
        return {
          language: lng,
          isPrimary: lng === props.customerDetails?.preferredLanguage,
        };
      })
    : [];

  const onSubmit = (data: any) => {
    props.onSubmit({ ...data, isAlghanimEmployee });
  };

  return props.customerDetails ? (
    <div className="pl-4 p-3 accordion__body border-bottom">
      <form onSubmit={handleSubmit(onSubmit)}>
        {!!props?.fields?.filter((x) => x.value === "gender")?.length &&
          !props.customerDetails?.gender && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Gender<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                name="gender"
                rules={{ required: true }}
                defaultValue={props.customerDetails?.gender}
                render={({ onChange }) => (
                  <RadioSelect
                    defaultValue={props.customerDetails?.gender}
                    options={[
                      { label: "Male", value: "male" },
                      { label: "Female", value: "female" },
                    ]}
                    optionLabel={"label"}
                    optionValue={"value"}
                    onChange={onChange}
                    disabled={!props.canUpdateCustomerDemographics}
                    name="gender"
                  />
                )}
              />

              {errors.gender && <ErrorMessage>Gender is required</ErrorMessage>}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "dob")?.length &&
          !props.customerDetails?.dob && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                {props.customerDetails?.isCompanyCustomer
                  ? "Registration Date"
                  : "Date of Birth"}{" "}
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="dob"
                defaultValue={props.customerDetails?.dob}
                render={({ onChange, value }) => (
                  <DateInput
                    default={value}
                    placeholder={
                      props.customerDetails?.isCompanyCustomer
                        ? "Registration Date"
                        : "Date of Birth"
                    }
                    onChange={onChange}
                    maxDate={new Date()}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
              {errors.dob && (
                <ErrorMessage>Date of birth is required</ErrorMessage>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "ageRange")?.length &&
          !props.customerDetails?.ageRange && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Age Range
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="ageRange"
                defaultValue={props.customerDetails?.ageRange}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={AgeLimit}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select Age Range"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
              {errors.ageRange && (
                <ErrorMessage>Age range is required</ErrorMessage>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "bloodGroup")?.length &&
          !props.customerDetails?.bloodGroup && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Blood Type
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="bloodGroup"
                defaultValue={props.customerDetails?.bloodGroup}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={bloodType}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select Blood Type"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
              {errors.bloodGroup && (
                <ErrorMessage>Blood Type is required</ErrorMessage>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "identifiers.nationality")
          ?.length &&
          !props.customerDetails?.identifiers?.nationality && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Nationality
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="nationality"
                defaultValue={props.customerDetails?.identifiers?.nationality}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={nationalities}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select Nationality"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    filter={"contains"}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
              {errors.nationality && (
                <ErrorMessage>Nationality is required</ErrorMessage>
              )}
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "profession")?.length &&
          !props.customerDetails?.profession && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Profession
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="profession"
                defaultValue={props.customerDetails?.profession}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    data={Occupations?.map((x) => {
                      return { name: capitalizeFirstLetter(x.name) };
                    })}
                    defaultValue={value}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select Profession"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    filter={"contains"}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "tags")?.length &&
          !props.customerDetails?.tags?.length && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Tag
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="tag"
                defaultValue={tag}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { value: "VIP", label: "VIP" },
                      { value: "Premium", label: "Premium" },
                    ]}
                    defaultValue={value}
                    textField={"label"}
                    valueField={"value"}
                    placeholder={"Select Tag"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "maritalStatus")?.length &&
          !props.customerDetails?.maritalStatus && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Marital Status
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="maritalStatus"
                defaultValue={props.customerDetails?.maritalStatus}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { value: "Single", label: "Single" },
                      { value: "Married", label: "Married" },
                      { value: "Divorced", label: "Divorced" },
                      { value: "Widow", label: "Widow" },
                    ]}
                    defaultValue={value}
                    textField={"label"}
                    valueField={"value"}
                    placeholder={"Select marital Status"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "incomeRange")?.length &&
          !props.customerDetails?.incomeRange && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Income Range (KD)
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="incomeRange"
                defaultValue={props.customerDetails?.incomeRange}
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={incomeRange}
                    textField={"name"}
                    valueField={"value"}
                    placeholder={"Select Income Range"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
              {errors.incomeRange && (
                <ErrorMessage>Income range is required</ErrorMessage>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "children")?.length &&
          !props.customerDetails?.children?.length && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Number of children
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="children"
                defaultValue={children}
                render={({ onChange, value }) => (
                  <CustomerChildren
                    className="form-control p-0 rounded-sm font-weight-semibold"
                    placeholder="Number Of Children"
                    value={value}
                    onChange={onChange}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "languagesSpeak")?.length &&
          !props.customerDetails?.languagesSpeak?.length && (
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                Language
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="languagesSpeak"
                defaultValue={languagesSpeak}
                render={({ onChange, value }) => (
                  <CustomerLanguage
                    className="form-control p-0 rounded-sm font-weight-semibold"
                    placeholder="Language"
                    value={value}
                    onChange={onChange}
                    disabled={!props.canUpdateCustomerDemographics}
                  />
                )}
              />
            </div>
          )}

        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            disabled={!props.canUpdateCustomerDemographics || props.isSaving}
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            title={
              !props.canUpdateCustomerDemographics
                ? "No permisssion to update Demographics"
                : ""
            }
          >
            Save
          </button>
        </div>
      </form>
    </div>
  ) : (
    <div className="p-4 accordion__body border-bottom"></div>
  );
};

export default DemographicForm;
